import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';
import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a cart icon.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} cartBubble - The cart bubble element.
 * @property {HTMLElement} cartBubbleText - The cart bubble text element.
 * @property {HTMLElement} cartBubbleCount - The cart bubble count element.
 *
 * @extends {Component<Refs>}
 */
class CartIcon extends Component {
  requiredRefs = ['cartBubble', 'cartBubbleText', 'cartBubbleCount'];

  /** @type {number} */
  get currentCartCount() {
    return parseInt(this.refs.cartBubbleCount.textContent ?? '0', 10);
  }

  set currentCartCount(value) {
    const oldValue = this.refs.cartBubbleCount.textContent;
    this.refs.cartBubbleCount.textContent = value < 100 ? String(value) : '';
    
    // Also update any other bubble count elements on the page (cart drawer, etc)
    const allBubbles = document.querySelectorAll('[ref="cartBubbleCount"]');
    allBubbles.forEach(bubble => {
      bubble.textContent = value < 100 ? String(value) : '';
    });
    
    if (oldValue !== String(value)) {
      const stack = new Error().stack;
    }
  }

  /**
   * Calculate adjusted cart count accounting for bundled items.
   * Bundles (4 items) should count as 1 item.
   * @returns {number} The adjusted item count
   */
  getAdjustedCartCount() {
    const totalItems = document.querySelectorAll('tr[data-key]').length;
    const bundleChildItems = document.querySelectorAll('.cart-items__bundle-child').length;
    return totalItems - bundleChildItems;
  }

  connectedCallback() {
    super.connectedCallback();

    // Check if we're on a cart page and update the count immediately
    const totalItems = document.querySelectorAll('tr[data-key]').length;
    if (totalItems > 0) {
      this.renderCartBubble(totalItems, false, false);
    }
    
    document.addEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.addEventListener('cart:count-update', this.onCartCountUpdate);
    this.ensureCartBubbleIsCorrect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.onCartUpdate);
    document.removeEventListener('cart:count-update', this.onCartCountUpdate);
  }

  /**
   * Handles custom cart count update event (for bundle grouping).
   * @param {CustomEvent} event - The cart count update event.
   */
  onCartCountUpdate = (event) => {
    const adjustedCount = event.detail?.count ?? 0;
    this.currentCartCount = adjustedCount;
    this.refs.cartBubbleCount.classList.toggle('hidden', adjustedCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', adjustedCount === 0);
  };

  /**
   * Handles the cart update event.
   * @param {CartUpdateEvent} event - The cart update event.
   */
  onCartUpdate = async (event) => {
    const itemCount = event.detail.data?.itemCount ?? 0;
    const comingFromProductForm = event.detail.data?.source === 'product-form-component';

    // Always use renderCartBubble - it will detect bundles via data-bundle-group attribute
    this.renderCartBubble(itemCount, comingFromProductForm);
  };

  /**
   * Renders the cart bubble.
   * @param {number} itemCount - The number of items in the cart.
   * @param {boolean} comingFromProductForm - Whether the cart update is coming from the product form.
   */
  renderCartBubble = async (itemCount, comingFromProductForm, animate = true) => {
    const rows = document.querySelectorAll('tr[data-key]');
    let displayCount;

    if (rows.length > 0) {
      // Cart page: count one per cover by excluding the wood/leather/hardware add-on
      // rows (and gifts), identified by their own properties. Robust even if a row is
      // missing its bundle-group tag.
      displayCount = 0;
      rows.forEach(row => {
        let skip = false;
        row.querySelectorAll('.cart-items__properties dt').forEach(dt => {
          const t = dt.textContent.trim();
          if (
            t === 'Wood Type:' ||
            t === 'Leather Color:' ||
            t === 'Hardware Finish:' ||
            t === '_gift_with_purchase:'
          ) {
            skip = true;
          }
        });
        if (!skip) displayCount += 1;
      });
    } else {
      // No cart rows on this page: fall back to the provided count (the API-based
      // cart-bubble-counter corrects bundle counts on non-cart pages).
      displayCount = comingFromProductForm ? this.currentCartCount + itemCount : itemCount;
    }

    this.refs.cartBubbleCount.classList.toggle('hidden', displayCount === 0);
    this.refs.cartBubble.classList.toggle('visually-hidden', displayCount === 0);
    this.refs.cartBubble.classList.toggle('cart-bubble--animating', displayCount > 0 && animate);

    const finalCount = displayCount;

    this.currentCartCount = finalCount;
  

    this.classList.toggle('header-actions__cart-icon--has-cart', displayCount > 0);

    sessionStorage.setItem(
      'cart-count',
      JSON.stringify({
        value: String(this.currentCartCount),
        timestamp: Date.now(),
      })
    );

    if (!animate) return;
    await onAnimationEnd(this.refs.cartBubbleText);

    this.refs.cartBubble.classList.remove('cart-bubble--animating');
  };

  /**
   * Checks if the cart count is correct.
   */
  ensureCartBubbleIsCorrect = () => {
    const sessionStorageCount = sessionStorage.getItem('cart-count');
    const visibleCount = this.refs.cartBubbleCount.textContent;

    if (sessionStorageCount === visibleCount || sessionStorageCount === null) return;

    try {
      const { value, timestamp } = JSON.parse(sessionStorageCount);

      if (Date.now() - timestamp < 10000) {
        const count = parseInt(value, 10);

        if (count >= 0) {
          this.renderCartBubble(count, false, false);
        }
      }
    } catch (_) {
      // no-op
    }
  };
}

if (!customElements.get('cart-icon')) {
  customElements.define('cart-icon', CartIcon);
}
